import React, { useEffect } from "react";
import { connect, ConnectedProps } from "react-redux";
import { useTranslation } from "react-i18next";
import Common from "us.common";
import { Formik } from "formik";
import { EditOutlined } from "us.icons";
import { FeeBasisHistory } from "../FeeBasisHistory";
import {
  DebtorType,
  DEBTOR_TYPE_OPTIONS,
} from "us.collection.admin/components/ManageFee/Constants";
import { useLocation, useNavigate } from "react-router-dom";
import * as Actions from "us.collection.admin/actions";
import { updateUrl } from "../../Functions";
import { IManageFee } from "us.collection.admin/components/ManageFee/Interfaces";
import { getSelectedFeeTypeNameForDrawerTitle } from "us.collection.admin/components/ManageFee/Functions";
import "../../Home.scss";
import { sortFeeBasicData } from "./Components";
import { $DateLabel, $DatePicker, $Select } from "us.common/components";
import { FeeRangeSW } from "../FeeRangeSW";
import { AddNewFeeBasis } from "../../Repository";
import { useReplaceNavigate } from "us.helper/hooks/customHooks/replaceHook/Home";
import { RootState } from "us.helper/types";
import { isArray } from "lodash";
import dayjs from "dayjs";

const { $Skeleton, $Button, $Tabs, $Col } = Common.Components;

/**
 * @description - Fee Basis Main view For SW
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/3116171308/Manage+Fee+-+UI+Implementation+Design
 * @author Tharanga Niroshana <tharangan@unicorn-solutions.com>
 * @since 26/08/2025
 */
const FeeBasisSW: React.FC<PropsFromRedux & IManageFee> = ({
  getFeeBasisData,
  feeBasis,
  onCallAction,
  feeType,
  currentDateFormat,
  addNewFeeBasis,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const replace = useReplaceNavigate();
  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const typeId = urlParams.get("typeId");
  const feeId = urlParams.get("feeId"); // this can be use

  const [isEdit, setIsEdit] = React.useState(false);

  const feeBasisItem = React.useMemo(() => {
    return isArray(feeBasis?.list) && feeBasis?.list.length > 0
      ? feeBasis.list[0]
      : {};
  }, [feeBasis?.list]);

  useEffect(() => {
    if (typeId) {
      getFeeBasisData && getFeeBasisData(typeId);
    }
  }, [typeId]);

  useEffect(() => {
    if (feeBasis.list.length > 0 && typeId != null) {
      const updatedFeeId =
        Number(feeId) == -1
          ? Number(feeId)
          : sortFeeBasicData(feeBasis?.list)[0]?.feeId;
      updateUrl(Number(typeId), replace, updatedFeeId);
    } else {
      const updatedFeeId = Number(feeId) == -1 ? Number(feeId) : 0;
      updateUrl(Number(typeId), replace, updatedFeeId);
    }
  }, [feeBasis.list]);

  /**
   * @description - Handle edit button click
   * @param values - Form values
   */
  const handleEditClick = (values: any) => {
    if (isEdit) {
      handleSubmit(values);
    }
    setIsEdit(!isEdit);
  };

  /**
   * @description - Handle form submit
   * @param {any} values - Form values
   * @param {any} actions - Form Actions
   */
  const handleSubmit = (values: any) => {
    const basisPayload = AddNewFeeBasis.call({
      ...values,
      typeId,
      feeId,
    });
    addNewFeeBasis && addNewFeeBasis(basisPayload);
  };

  return (
    <Formik enableReinitialize initialValues={feeBasisItem} onSubmit={() => {}}>
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValidating,
        ...restProps
      }: any) => (
        <>
          <$Tabs
            className="bui-tabs"
            defaultActiveKey={"COLLECTION_FEE_BASIS"}
            onTabClick={(key: any) => {
              updateUrl(
                Number(typeId),
                replace,
                key === "COLLECTION_FEE_BASIS" ? feeBasisItem?.feeId : -1
              );
            }}
          >
            {/* feeBasisLayout */}
            <$Tabs.TabPane
              tab={
                <>
                  {`${
                    getSelectedFeeTypeNameForDrawerTitle(
                      feeType?.list,
                      Number(typeId)
                    ) ?? ""
                  }${" "}`}
                  {t("US.COLLECTION.ADMIN:MANAGE_FEE.BASIS")}
                </>
              }
              key="COLLECTION_FEE_BASIS"
              className="px-3"
            >
              {/* <FeeBasisDetails/> */}
              <$Skeleton
                loading={feeBasis?.isListLoading}
                active
                paragraph={{ rows: 2 }}
              >
                <>
                  <div
                    style={{
                      display: "flex",
                      gap: "2rem",
                      alignItems: "flex-start",
                    }}
                  >
                    <div className="d-flex">
                      <div className="mr-5" style={{ minWidth: 160 }}>
                        <div>{t("US.COMMON:COMMON.APPLICABLE_DATE")}</div>
                        <div>
                          {!isEdit && (
                            <strong className="pb-1">
                              <$DateLabel value={feeBasisItem?.fromDate} />
                            </strong>
                          )}
                          {isEdit && (
                            <$DatePicker
                              allowClear
                              placeholder={t(
                                "US.COLLECTION.COMMON:COMMON.SELECT_DATE"
                              )}
                              name="fromDate"
                              format={currentDateFormat}
                              size="small"
                              defaultPickerValue={dayjs()}
                              value={dayjs(values?.fromDate)}
                              className="w-100"
                              required
                              data-testid="fee-date"
                            />
                          )}
                        </div>
                      </div>
                      <div className="mr-5" style={{ minWidth: 90 }}>
                        <div>
                          {t("US.COLLECTION.ADMIN:MANAGE_FEE.DEBTOR_TYPE")}
                        </div>
                        <div>
                          {!isEdit && feeBasisItem ? (
                            <strong className="pb-1">
                              {feeBasisItem?.debtorType}
                            </strong>
                          ) : (
                            ""
                          )}
                          {isEdit && (
                            <$Select
                              name="debtorType"
                              allOption={false}
                              required
                              defaultValue={DebtorType.P}
                              options={DEBTOR_TYPE_OPTIONS}
                              data-testid="debtor_Type"
                              className="mt-1 w-100"
                              dropdownMatchSelectWidth={true}
                            />
                          )}
                        </div>
                      </div>
                      <div className="d-flex align-items-end pb-1">
                        <$Button
                          onClick={() => handleEditClick(values)}
                          type="dashed"
                          size="small"
                          id="add-new-basis"
                          icon={<EditOutlined />}
                        >
                          {isEdit
                            ? t("US.COMMON:COMMON.SAVE")
                            : t("US.COMMON:COMMON.EDIT")}
                        </$Button>
                      </div>
                      {isEdit && (
                        <div className="d-flex align-items-end pb-1 ml-2">
                          <$Button
                            onClick={() => setIsEdit(!isEdit)}
                            type="dashed"
                            size="small"
                            id="cancel-new-basis"
                          >
                            {t("US.COMMON:COMMON.CANCEL")}
                          </$Button>
                      </div>
                      )}
                    </div>
                  </div>

                  {/* feeRangeLayout */}
                  {Number(feeId) != -1 && (
                    <FeeRangeSW onCallAction={onCallAction} />
                  )}
                </>
              </$Skeleton>
            </$Tabs.TabPane>
            {/* feeBasisHistoryLayout */}
            <$Tabs.TabPane
              tab={<>{t("US.COLLECTION.ADMIN:MANAGE_FEE.HISTORY")}</>}
              key="HISTORY"
              className="px-3"
            >
              <FeeBasisHistory onCallAction={onCallAction} />
            </$Tabs.TabPane>
          </$Tabs>
        </>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { manageFee, common } = state;
  const { feeBasis, feeType } = manageFee;
  const { currentDateFormat } = common;
  return {
    feeBasis,
    feeType,
    currentDateFormat,
  };
};
const { feeBasisActions, newFeeBasis } = Actions.manageFee;
const { getById } = feeBasisActions;

const mapDispatchToProps = {
  getFeeBasisData: getById,
  addNewFeeBasis: newFeeBasis.save,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(FeeBasisSW);
